import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useMediaQuery } from 'react-responsive';

// Import our custom hooks
import { useSurveyData } from '../hooks/useSurveyData';
import { useSurveyForm } from '../hooks/useSurveyForm';
import { useAutoSave } from '../hooks/useAutoSave';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useUserAuth } from '../../../context/UserAuthContext';

// Import components
import SurveyTabs from './SurveyTabs';
import SavedSurveyDialog from './SavedSurveyDialog';
import CompletionDialog from './CompletionDialog';
import IncompleteDialog from './IncompleteDialog';
import SubmissionErrorDialog from './SubmissionErrorDialog';
import ValidationDialog from './ValidationDialog';

// Import existing components that we'll keep
import Header from '../../../components/Header/Header';
import Logo from '../../../components/Logo/Logo';
import PageTitle from '../../../components/PageTitle/PageTitle';

// Import styles
import '../FacilitiesWalkthrough.css';

/**
 * Main FacilitiesWalkthrough Component - Refactored
 *
 * This is the orchestrator component that manages the overall survey workflow.
 * It's been significantly simplified by extracting logic into custom hooks and
 * breaking down the UI into focused sub-components.
 *
 * Key responsibilities:
 * - Initialize survey data and form state
 * - Coordinate between different hooks
 * - Handle high-level user interactions (dialogs, navigation)
 * - Provide loading and error states
 *
 * Save Behavior:
 * - Saves automatically when user navigates (tab changes, next/back buttons)
 * - Saves on final form submission
 * - Does NOT save on individual field changes (removed auto-save on input)
 */
const FacilitiesWalkthroughNew = () => {
  const navigate = useNavigate();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const { user: firebaseUser } = useUserAuth();

  // Initialize survey ID
  const [surveyId, setSurveyId] = useState(() => uuidv4());

  // Dialog states
  const [showSavedDialog, setShowSavedDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showIncompleteDialog, setShowIncompleteDialog] = useState(false);
  const [showSubmissionErrorDialog, setShowSubmissionErrorDialog] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [completionType, setCompletionType] = useState('submitted'); // 'submitted' or 'saved'

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Missing items for incomplete dialog
  const [missingItems, setMissingItems] = useState([]);

  // Validation state for section transitions
  const [validationMissingItems, setValidationMissingItems] = useState([]);
  const [currentSection, setCurrentSection] = useState(1);
  const [targetSection, setTargetSection] = useState(1);

  // Survey state management
  const [isNewSurvey, setIsNewSurvey] = useState(true);
  const [globalMessage] = useState('');


  // Initialize custom hooks
  const {
    surveyInfo,
    userInfo,
    stores,
    savedAnswers,
    savedImages,
    frIDs,
    loading: dataLoading,
    error: dataError,
    updateSelectedStore,
    fetchSavedAnswers,
    archiveSavedSurvey,
    setFrIDs,
    setSavedAnswers,
  } = useSurveyData();

  const {
    control,
    handleSubmit,
    getFormDataForSubmission,
    getFilesForUpload,
    validateSurveyCompletion,
    validateSection,
    getSurveyCompletionDetails,
    loadSavedData,
    mergeSavedData,
    formMethods,
    errors,
  } = useSurveyForm();

  const {
    isSaving,
    saveStatus,
    saveImmediately,
    getSaveStatusMessage,
    initializePreviousData,
    isNetworkError,
    clearSaveError,
  } = useAutoSave(surveyId, userInfo, frIDs, setFrIDs);

  const { connectionQuality, showWarning, dismissWarning } = useNetworkStatus();

  /**
   * Handles store selection changes
   * Updates the user info and triggers data refresh for the new store
   */
  const handleStoreChange = (event) => {
    const { value } = event.target;
    const selectedStore = stores.find((s) => s.storeId === value);

    if (selectedStore) {
      updateSelectedStore(selectedStore.storeId, selectedStore.storeName);

      // Reset survey state for new store
      setIsNewSurvey(true);
      setSurveyId(uuidv4());
      setShowSavedDialog(false); // Close any existing dialog
      setActiveTabIndex(0); // Reset to first tab
      setCurrentSection(1); // Reset to first section

      // Clear any existing form data
      formMethods.reset({}, { keepDefaultValues: true });

      // Reset previous data for new store
      initializePreviousData(null);


      // Check for saved answers in the newly selected store
      // Use setTimeout to ensure the store update has been processed
      setTimeout(() => {
        fetchSavedAnswers(1); // Check section 1 for saved answers
      }, 100);
    }
  };

  /**
   * Handles the saved survey dialog - when user chooses to restore or start new
   */
  const handleSavedSurveyChoice = (choice) => {
    if (choice === 'restore') {
      // Extract survey ID from saved answers to maintain consistency
      const savedSurveyId = savedAnswers?.[0]?.fwtId;
      if (savedSurveyId && savedSurveyId !== surveyId) {
        setSurveyId(savedSurveyId);
      }

      // Load saved data into form
      const newFrIDs = loadSavedData(savedAnswers, savedImages);
      setFrIDs(newFrIDs);
      setIsNewSurvey(false);

      // Initialize previous data with the loaded saved data
      const formData = getFormDataForSubmission();
      initializePreviousData(formData);

    } else if (choice === 'start_new') {
      // Archive existing saved data and start fresh
      archiveSavedSurvey();
      formMethods.reset({}, { keepDefaultValues: true });
      setIsNewSurvey(true);
      setSurveyId(uuidv4());
      setActiveTabIndex(0); // Reset to first tab
      setCurrentSection(1); // Reset to first section

      // Reset previous data for new survey
      initializePreviousData(null);

    }

    setShowSavedDialog(false);
  };

  /**
   * Handles form submission (final submit, not auto-save)
   */
  const handleFinalSubmit = async () => {
    try {
      // First validate completeness with detailed feedback
      const completionDetails = getSurveyCompletionDetails();

      if (!completionDetails.isComplete) {
        setMissingItems(completionDetails.missingItems);
        setShowIncompleteDialog(true);
        return;
      }

      // Set submitting state to show continuous loading
      setIsSubmitting(true);

      // Get form data for submission
      const submissionData = getFormDataForSubmission();
      
      
      // Get files for upload
      const filesToUpload = getFilesForUpload();

      // Perform a final save to ensure all data is in the database
      // Use saveChangedOnly=false for final submission to ensure everything is saved
      await saveImmediately(submissionData, filesToUpload, true, false);

      // Add a small delay to ensure database write is complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Then submit the survey (this will archive the saved data)
      await submitSurvey(submissionData);

      // Clear saved answers from state since survey is now submitted
      setSavedAnswers(null);

      setCompletionType('submitted');
      setShowCompletionDialog(true);
    } catch (error) {
      console.error('Survey submission failed:', {
        surveyId,
        userEmail: firebaseUser?.email,
        store: userInfo?.defaultStore,
        error: error.message
      });
      
      // Show submission error dialog since data was saved but submission failed
      setShowSubmissionErrorDialog(true);
    } finally {
      // Always clear submitting state
      setIsSubmitting(false);
    }
  };

  /**
   * Submits the survey to the backend (final submission)
   * This archives the saved survey data and processes it for final submission
   */
  const submitSurvey = async (formData) => {
    try {
      if (!userInfo.defaultStore || !firebaseUser?.email) {
        throw new Error('Missing required data for survey submission');
      }


      // Filter out empty values and prepare data for submission
      // IMPORTANT: Keep responsible and explain fields for proper submission
      const filteredData = formData
        .filter(item => {
          // Keep items with values, or items that have responsible/explain data
          const hasValue = item.value !== undefined && item.value !== null && item.value !== '';
          const hasResponsible = item.responsible !== undefined && item.responsible !== null && item.responsible !== '';
          const hasExplain = item.explain !== undefined && item.explain !== null && item.explain !== '';
          return hasValue || hasResponsible || hasExplain;
        })
        .map(item => ({
          question: item.question,
          value: item.value,
          responsible: item.responsible || '',
          explain: item.explain || ''
        }));



      // Get Firebase token for authentication
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      // Submit the survey to the backend
      const response = await axios.post('/hd-api/facilities-walkthrough/submit', {
        fwtId: surveyId,
        storeId: userInfo.defaultStore,
        userEmail: firebaseUser.email,
        data: filteredData,
      }, {
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Submit survey API call failed:', {
        userEmail: firebaseUser?.email,
        store: userInfo?.defaultStore,
        error: error.message,
        status: error.response?.status
      });
      throw error;
    }
  };

  // State to track active tab index for controlled tabs component
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  /**
   * Handles tab changes with validation and immediate save
   * This is one of the main save triggers - saves when user navigates between sections
   * Now includes section-by-section validation.
   * Navigation is blocked until the current section saves successfully.
   */
  const handleTabChange = async (_, newTabIndex) => {
    const newSection = newTabIndex + 1; // Convert 0-based index to 1-based section
    const currentSectionNum = currentSection;

    // Don't validate if staying on the same section
    if (newSection === currentSectionNum) {
      // Still update the tab index for consistency
      setActiveTabIndex(newTabIndex);
      return;
    }

    // Validate current section before allowing navigation
    const validation = validateSection(currentSectionNum, surveyInfo);
    if (!validation.isValid) {
      // Show validation dialog and prevent navigation
      setValidationMissingItems(validation.missingItems);
      setTargetSection(newSection);
      setShowValidationDialog(true);
      return; // Stop navigation - don't update tab index
    }

    // Save current section BEFORE navigating — block navigation on failure
    const currentData = getFormDataForSubmission();
    const filesToUpload = getFilesForUpload();

    if (currentData.length > 0 || filesToUpload.length > 0) {
      try {
        await saveImmediately(currentData, filesToUpload, false, true);
      } catch (error) {
        // Save failed — useAutoSave sets saveStatus to 'error' which triggers
        // the error toast. Keep the user on the current section so they can
        // retry once connectivity is restored.
        return;
      }
    }

    // Save succeeded — proceed with tab change
    setActiveTabIndex(newTabIndex);
    await performTabChange(newSection);
  };

  /**
   * Performs the actual tab change logic after save + validation pass
   */
  const performTabChange = async (newSection) => {
    // Update current section
    setCurrentSection(newSection);

    // Scroll to top of page when changing sections
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Load saved data for the new section if not a new survey
    if (!isNewSurvey) {
      const sectionAnswers = await fetchSavedAnswers(newSection);
      if (sectionAnswers && sectionAnswers.length > 0) {
        // Merge the fetched answers into the form without overwriting other sections
        const newFrIDs = mergeSavedData(sectionAnswers, savedImages);
        setFrIDs((prevFrIDs) => ({ ...prevFrIDs, ...newFrIDs }));

        // Update the previous data reference to include the newly loaded data
        // This prevents the change detection from thinking this is a "change"
        setTimeout(() => {
          const updatedFormData = getFormDataForSubmission();
          initializePreviousData(updatedFormData);
        }, 100);
      }
    }
  };

  /**
   * Handles validation dialog closure
   */
  const handleValidationDialogClose = () => {
    setShowValidationDialog(false);
    // Reset target section since user cancelled navigation
    setTargetSection(currentSection);
  };

  // Note: Removed form watcher that was causing focus loss and page jumping
  // The auto-save system handles change detection without needing to track dirty state

  /**
   * Check for saved answers when user info loads with a default store
   */
  useEffect(() => {
    if (userInfo.defaultStore && isNewSurvey && !savedAnswers) {
      fetchSavedAnswers(1);
    }
  }, [userInfo.defaultStore, isNewSurvey, savedAnswers, fetchSavedAnswers]);

  /**
   * Synchronize currentSection with activeTabIndex
   */
  useEffect(() => {
    const newSection = activeTabIndex + 1;
    if (newSection !== currentSection) {
      setCurrentSection(newSection);
    }
  }, [activeTabIndex, currentSection]);

  /**
   * Handle saved answers detection - show dialog when saved answers are found
   */
  useEffect(() => {
    if (savedAnswers && savedAnswers.length > 0 && isNewSurvey) {
      setShowSavedDialog(true);
    }
  }, [savedAnswers, isNewSurvey]);

  /**
   * Show toast when survey is saved
   */
  useEffect(() => {
    if (saveStatus === 'saved') {
      setToastMessage('Survey saved successfully');
      setShowToast(true);
    } else if (saveStatus === 'error') {
      setToastMessage(
        isNetworkError
          ? 'Unable to reach the server. Please check your connection and retry.'
          : 'Error saving survey'
      );
      setShowToast(true);
    }
  }, [saveStatus, isNetworkError]);

  /**
   * Handle completion dialog closure
   */
  const handleCompletionDialogClose = () => {
    setShowCompletionDialog(false);
    navigate('/home');
  };

  /**
   * Manual save triggered by the Save button in the section footer
   */
  const handleManualSave = async () => {
    const currentData = getFormDataForSubmission();
    const filesToUpload = getFilesForUpload();
    await saveImmediately(currentData, filesToUpload, true, false);
  };

  /**
   * Retry save after a network error
   */
  const handleRetrySave = async () => {
    setShowToast(false);
    try {
      await saveImmediately(getFormDataForSubmission(), getFilesForUpload(), true, true);
    } catch (e) {
      // Error handling already managed by useAutoSave
    }
  };

  /**
   * Handle toast close
   */
  const handleToastClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowToast(false);
    // If user dismisses a network error toast, clear the error state so the
    // UI (spinner, status text) resets while retries continue in the background.
    if (isNetworkError) {
      clearSaveError();
    }
  };

  /**
   * Gets the appropriate status message based on current state
   */
  const getStatusMessage = () => {
    if (isSubmitting) {
      return 'Submitting survey...';
    }
    return getSaveStatusMessage();
  };

  /**
   * Determines if any loading indicator should be shown
   */
  const isShowingStatus = isSubmitting || saveStatus !== 'idle';

  // Show loading state while initializing
  if (dataLoading) {
    return (
      <Box p={2}>
        <Header isTabletOrMobile={isTabletOrMobile} />
        <Container
          maxWidth='md'
          style={{ textAlign: 'center', marginTop: '2rem' }}
        >
          <CircularProgress />
          <Typography variant='body1' sx={{ mt: 2 }}>
            Loading survey...
          </Typography>
        </Container>
      </Box>
    );
  }

  // Show error state if data failed to load
  if (dataError) {
    return (
      <Box p={2}>
        <Header isTabletOrMobile={isTabletOrMobile} />
        <Container
          maxWidth='md'
          style={{ textAlign: 'center', marginTop: '2rem' }}
        >
          <Typography variant='h6' color='error' gutterBottom>
            Error Loading Survey
          </Typography>
          <Typography variant='body1'>{dataError}</Typography>
          <Typography variant='body2' sx={{ mt: 2 }}>
            Please refresh the page and try again.
          </Typography>
        </Container>
      </Box>
    );
  }

  // Show store selection prompt if no store selected
  if (!userInfo.defaultStore) {
    return (
      <Box p={2}>
        <Header isTabletOrMobile={isTabletOrMobile} />
        <Container maxWidth='md' style={{ textAlign: 'center' }}>
          <Logo isTabletOrMobile={isTabletOrMobile} showAlert={globalMessage} />

          <PageTitle
            formData={userInfo}
            handleChange={handleStoreChange}
            stores={stores}
          />
          <Paper sx={{ p: 4, mt: 2 }}>
            <Typography variant='h6' gutterBottom>
              Please select a store to begin the facilities walkthrough.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Main survey interface
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        minHeight: '100vh',
      }}
    >
      {/* Fixed Header Section */}
      <Box sx={{ flexShrink: 0 }}>
        <Header isTabletOrMobile={isTabletOrMobile} />
        <Container maxWidth='md' style={{ padding: 12 }}>
          <div style={{ padding: '0 16px' }}>
            <Logo
              isTabletOrMobile={isTabletOrMobile}
              showAlert={globalMessage}
            />
          </div>

          <PageTitle
            formData={userInfo}
            handleChange={handleStoreChange}
            stores={stores}
          />

          {/* Save/Submit status indicator */}
          <Box sx={{ mt: 2, textAlign: 'center', minHeight: '40px' }}>
            <Typography
              variant='caption'
              color={isSubmitting ? 'primary' : 'textSecondary'}
              sx={{ 
                visibility: isShowingStatus ? 'visible' : 'hidden',
                fontWeight: isSubmitting ? 'bold' : 'normal'
              }}
            >
              {getStatusMessage()}
            </Typography>
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                justifyContent: 'center',
                visibility: (saveStatus === 'saving' || isSubmitting) ? 'visible' : 'hidden',
              }}
            >
              <CircularProgress size={16} />
            </Box>
          </Box>
        </Container>
      </Box>

      {showWarning && (
        <Container maxWidth='md' sx={{ px: 1.5 }}>
          <Alert
            severity={connectionQuality === 'offline' ? 'error' : 'warning'}
            onClose={dismissWarning}
          >
            {connectionQuality === 'offline'
              ? 'You are currently offline. Your answers will not be saved until your connection is restored.'
              : 'Weak network connection detected. Auto-save may be unreliable.'}
          </Alert>
        </Container>
      )}

      {/* Scrollable Form Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Container maxWidth='md' style={{ padding: 12, height: '100%' }}>
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: '4px',
              margin: '24px 0',
            }}
          >
            <form
              onSubmit={handleSubmit(handleFinalSubmit)}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {surveyInfo && (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <SurveyTabs
                    control={control}
                    errors={errors}
                    surveyInfo={surveyInfo}
                    onTabChange={handleTabChange}
                    onSave={handleManualSave}
                    isLoading={isSaving || isSubmitting}
                    value={activeTabIndex}
                  />
                </Box>
              )}
            </form>
          </Paper>
        </Container>
      </Box>

      {/* Dialogs */}
      <SavedSurveyDialog
        open={showSavedDialog}
        onChoice={handleSavedSurveyChoice}
      />

      <CompletionDialog
        open={showCompletionDialog}
        type={completionType}
        onClose={handleCompletionDialogClose}
      />

      <IncompleteDialog
        open={showIncompleteDialog}
        onClose={() => setShowIncompleteDialog(false)}
        missingItems={missingItems}
      />

      <SubmissionErrorDialog
        open={showSubmissionErrorDialog}
        onClose={() => setShowSubmissionErrorDialog(false)}
      />

      <ValidationDialog
        open={showValidationDialog}
        onClose={handleValidationDialogClose}
        missingItems={validationMissingItems}
        currentSection={currentSection}
        targetSection={targetSection}
        surveyInfo={surveyInfo}
      />

      {/* Toast notification for save status */}
      <Snackbar
        open={showToast}
        autoHideDuration={saveStatus === 'error' ? null : 3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleToastClose}
          severity={saveStatus === 'error' ? 'error' : 'success'}
          sx={{ width: '100%' }}
          action={
            isNetworkError ? (
              <Button color="inherit" size="small" onClick={handleRetrySave}>
                Retry
              </Button>
            ) : undefined
          }
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilitiesWalkthroughNew;
